import { tokens } from "@/tokens";
import React, { useState } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Checkbox,
  Alert,
  Button,
  Stack,
  Typography,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import DeleteIcon from '@/assets/svgs/garbage.svg';
import { TextInput } from '@/components';
import { ValidationIssue } from './validation-utils';
import OriginCriterionSelect from '../dropdown-components/origin-criterion-select';
import { formatHTSCode } from '@/utils';

interface ItemsEditTableProps {
  items: CertificateItem[];
  onUpdate: (items: CertificateItem[]) => void;
  formType: string;
  validationIssues: ValidationIssue[];
}

const ItemsEditTable: React.FC<ItemsEditTableProps> = ({
  items,
  onUpdate,
  formType,
  validationIssues,
}) => {
  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  
  // Split items into included and excluded
  const includedItems = items.filter(item => item.INCLUDED);
  const excludedItems = items.filter(item => !item.INCLUDED);

  const handleItemChange = (index: number, field: keyof CertificateItem, value: any) => {
    const updatedItems = [...items];
    
    if (field === 'INCLUDED') {
      updatedItems[index] = { ...updatedItems[index], [field]: value as boolean };
    } else if (field === 'hts' && value) {
      // Format HTS code
      updatedItems[index] = { ...updatedItems[index], [field]: formatHTSCode(value.replace(/\D/g, "")) };
    } else {
      updatedItems[index] = { ...updatedItems[index], [field]: value };
    }
    
    // Auto-include items from Canada/Mexico for USMCA
    if (formType === 'USMCA' && field === 'origin') {
      const origin = value?.toUpperCase();
      if (origin === 'CA' || origin === 'MX' || origin === 'US') {
        updatedItems[index] = { ...updatedItems[index], INCLUDED: true };
      }
    }
    
    onUpdate(updatedItems);
  };

  const handleAddItem = () => {
    const newItem: CertificateItem = {
      partNumber: '',
      description: '',
      hts: '',
      invoiceNumber: '',
      originCriterion: '',
      origin: '',
      INCLUDED: false,
    };
    
    onUpdate([...items, newItem]);
    setExpandedRows([...expandedRows, items.length]);
  };

  const handleRemoveItem = (index: number) => {
    const updatedItems = items.filter((_, i) => i !== index);
    onUpdate(updatedItems);
    setExpandedRows(expandedRows.filter(row => row !== index).map(row => row > index ? row - 1 : row));
  };

  const toggleRowExpansion = (index: number) => {
    if (expandedRows.includes(index)) {
      setExpandedRows(expandedRows.filter(row => row !== index));
    } else {
      setExpandedRows([...expandedRows, index]);
    }
  };

  const getItemIssues = (index: number) => {
    return validationIssues.filter(issue => issue.field.startsWith(`items.${index}.`));
  };

  const getFieldError = (index: number, field: string) => {
    return validationIssues.find(
      issue => issue.field === `items.${index}.${field}` && issue.type === 'error'
    );
  };

  const renderItemsTable = (tableItems: CertificateItem[], title: string, isIncluded: boolean) => {
    // Find the actual indices in the original items array
    const getOriginalIndex = (tableIndex: number) => {
      const item = tableItems[tableIndex];
      return items.findIndex(i => i === item);
    };
    
    return (
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ color: isIncluded ? tokens.status.success : tokens.text.secondary }}>
          {title} ({tableItems.length} items)
        </Typography>
        
        <TableContainer component={Paper} sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Typography variant="caption" fontWeight={tokens.fontWeight.bold}>
                    Include
                  </Typography>
                </TableCell>
                <TableCell>Part Number</TableCell>
                <TableCell>Description{isIncluded ? ' *' : ''}</TableCell>
                <TableCell>HTS Code{isIncluded ? ' *' : ''}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    Invoice #
                    <Tooltip title="Optional - Include if available">
                      <WarningIcon sx={{ fontSize: 16, color: tokens.status.warning }} />
                    </Tooltip>
                  </Box>
                </TableCell>
                <TableCell>Origin{isIncluded ? ' *' : ''}</TableCell>
                <TableCell>Criterion{isIncluded ? ' *' : ''}</TableCell>
                <TableCell width={100} align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tableItems.map((item, tableIndex) => {
                const index = getOriginalIndex(tableIndex);
                const itemIssues = getItemIssues(index);
                const hasErrors = itemIssues.some(issue => issue.type === 'error');
                const hasWarnings = itemIssues.some(issue => issue.type === 'warning');
                const isExpanded = expandedRows.includes(index);

                return (
                  <React.Fragment key={index}>
                  <TableRow 
                    hover 
                    sx={{ 
                      '& > *': { borderBottom: isExpanded ? 'none' : undefined },
                      backgroundColor: hasErrors ? '#ffebee' : undefined,
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={item.INCLUDED || false}
                        onChange={(e) => handleItemChange(index, 'INCLUDED', e.target.checked)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <TextInput
                        type="text"
                        value={item.partNumber || ''}
                        handleChangeValue={(val) => handleItemChange(index, 'partNumber', val)}
                        placeholder="e.g., ELEC-001"
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextInput
                        type="text"
                        value={item.description || ''}
                        handleChangeValue={(val) => handleItemChange(index, 'description', val)}
                        placeholder="Product description"
                        size="small"
                        fullWidth
                        error={!!getFieldError(index, 'description')}
                      />
                    </TableCell>
                    <TableCell>
                      <TextInput
                        type="text"
                        value={item.hts || ''}
                        handleChangeValue={(val) => handleItemChange(index, 'hts', val)}
                        placeholder="0000.00.0000"
                        size="small"
                        fullWidth
                        error={!!getFieldError(index, 'hts')}
                      />
                    </TableCell>
                    <TableCell>
                      <TextInput
                        type="text"
                        value={item.invoiceNumber || ''}
                        handleChangeValue={(val) => handleItemChange(index, 'invoiceNumber', val)}
                        placeholder="INV-001"
                        size="small"
                        fullWidth
                      />
                    </TableCell>
                    <TableCell>
                      <TextInput
                        type="text"
                        value={item.origin || ''}
                        handleChangeValue={(val) => handleItemChange(index, 'origin', val)}
                        placeholder="e.g., US"
                        size="small"
                        fullWidth
                        error={!!getFieldError(index, 'origin')}
                      />
                    </TableCell>
                    <TableCell width={150}>
                      <OriginCriterionSelect
                        value={item.originCriterion || ''}
                        onChange={(val) => handleItemChange(index, 'originCriterion', val)}
                        size="small"
                        error={!!getFieldError(index, 'originCriterion')}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={0.5} justifyContent="center">
                        {(hasErrors || hasWarnings) && (
                          <Tooltip title={`${itemIssues.length} issue(s)`}>
                            <IconButton 
                              size="small" 
                              onClick={() => toggleRowExpansion(index)}
                              color={hasErrors ? 'error' : 'warning'}
                            >
                              {hasErrors ? <ErrorIcon fontSize="small" /> : <WarningIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        )}
                          <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveItem(index)}
                        >
                          <img src={DeleteIcon} alt="Delete" style={{ width: 20, height: 20 }} />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>
                  
                  {isExpanded && (
                    <TableRow>
                      <TableCell colSpan={8} sx={{ py: 2, backgroundColor: '#f5f5f5' }}>
                        <Box sx={{ px: 2 }}>
                          {itemIssues.length > 0 && (
                            <Stack spacing={1}>
                              {itemIssues.map((issue, idx) => (
                                <Alert key={idx} severity={issue.type} sx={{ py: 0 }}>
                                  {issue.message}
                                </Alert>
                              ))}
                            </Stack>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  return (
    <Box>
      {formType === 'USMCA' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Items with origin from Canada (CA), Mexico (MX), or USA (US) are automatically included for USMCA.
        </Alert>
      )}
      
      <Alert severity="warning" sx={{ mb: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: tokens.fontWeight.bold }}>
          Invoice Number is Optional
        </Typography>
        <Typography variant="body2">
          Include invoice numbers if available. This helps with traceability but is not required for certificate generation.
        </Typography>
      </Alert>

      {/* Included Items Table */}
      {includedItems.length > 0 && renderItemsTable(includedItems, 'Included Items', true)}
      
      {/* Excluded Items Table */}
      {excludedItems.length > 0 && renderItemsTable(excludedItems, 'Excluded Items', false)}
      
      {/* Show message if no items */}
      {items.length === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No items have been added yet. Click "Add Item" to begin.
        </Alert>
      )}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleAddItem}
        fullWidth
        sx={{ fontWeight: tokens.fontWeight.semibold, fontSize: tokens.fontSize.md, borderRadius: tokens.radius.md, height: 36, color: tokens.text.body, bgcolor: tokens.bg.surface, borderColor: tokens.border.default, "&:hover": { borderColor: tokens.text.disabled, bgcolor: tokens.bg.hover } }}
      >
        Add Item
      </Button>
    </Box>
  );
};

export default ItemsEditTable;